import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Progress } from "./ui/progress";
import { Semaforo, StatusBadge } from "./Semaforo";
import { Link } from "react-router-dom";
import { formatCLP, formatDate, formatLabel } from "../lib/format";
import {
  CheckCircle, Clock, Warning, ListChecks, Briefcase, Calendar,
  CurrencyCircleDollar, Users, TrendUp,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";

const today = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const daysLeft = (dateStr) => {
  if (!dateStr) return null;
  const t = today();
  return Math.ceil((new Date(dateStr).getTime() - t.getTime()) / 86400000);
};

function MiniStat({ icon: Icon, label, value, hint, tone = "slate" }) {
  const tones = {
    slate: { card: "border-slate-200 bg-white text-slate-900", icon: "text-brand-orange-500" },
    emerald: { card: "border-brand-green-200 bg-brand-green-50/50 text-emerald-900", icon: "text-brand-green-600" },
    amber: { card: "border-amber-200 bg-amber-50/50 text-amber-900", icon: "text-amber-600" },
    red: { card: "border-red-200 bg-red-50/50 text-red-900", icon: "text-red-600" },
    sky: { card: "border-sky-200 bg-sky-50/50 text-sky-900", icon: "text-sky-600" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className={`rounded-md border p-4 ${t.card}`}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.15em] text-slate-500">
        <span>{label}</span>
        <Icon size={18} weight="duotone" className={t.icon} />
      </div>
      <div className="mt-2 text-2xl font-bold font-display">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function DistroBar({ rows, total }) {
  if (!total) return <div className="text-sm text-slate-400">Sin datos.</div>;
  return (
    <div className="space-y-2.5">
      {rows.map((r) => {
        const pct = Math.round((r.value / total) * 100);
        return (
          <div key={r.label} className="text-sm">
            <div className="flex justify-between mb-1">
              <span className="text-slate-700">{r.label}</span>
              <span className="text-slate-500 font-mono">{r.value} <span className="text-slate-400">· {pct}%</span></span>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full ${r.color || "bg-slate-700"}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Convert tailwind bg-* class to a hex used by recharts.
const COLOR_MAP = {
  "bg-brand-green-500": "#85c536", "bg-brand-green-600": "#6ba627",
  "bg-emerald-500": "#85c536", "bg-emerald-600": "#6ba627",
  "bg-sky-500": "#0ea5e9", "bg-sky-600": "#0284c7",
  "bg-amber-500": "#f59e0b", "bg-amber-600": "#d97706",
  "bg-red-500": "#ef4444", "bg-red-700": "#b91c1c",
  "bg-slate-400": "#94a3b8", "bg-slate-500": "#64748b", "bg-slate-700": "#3a3939",
  "bg-indigo-500": "#6366f1", "bg-fuchsia-500": "#d946ef",
  "bg-teal-500": "#14b8a6", "bg-rose-500": "#f43f5e",
  "bg-cyan-600": "#0891b2", "bg-violet-600": "#7c3aed",
  "bg-brand-orange-500": "#ff751f", "bg-brand-orange-600": "#e55d10",
};
const hexOf = (cls) => COLOR_MAP[cls] || "#3a3939";

export default function CommunityDashboard({ community, commitments, projects, meetings }) {
  const k = useMemo(() => {
    const overdue = commitments.filter((c) => {
      const dl = daysLeft(c.fecha_compromiso);
      return dl !== null && dl < 0 && c.estado !== "cumplido";
    });
    const due30 = commitments.filter((c) => {
      const dl = daysLeft(c.fecha_compromiso);
      return dl !== null && dl >= 0 && dl <= 30 && c.estado !== "cumplido";
    });
    const completed = commitments.filter((c) => c.estado === "cumplido");
    const completionRate = commitments.length
      ? Math.round((completed.length / commitments.length) * 100)
      : 0;
    const avgAvance = commitments.length
      ? Math.round(commitments.reduce((s, c) => s + (Number(c.percent_avance) || 0), 0) / commitments.length)
      : 0;
    const avgProjAvance = projects.length
      ? Math.round(projects.reduce((s, p) => s + (Number(p.percent_avance) || 0), 0) / projects.length)
      : 0;
    const totalBeneficiarios = commitments.reduce(
      (s, c) => s + (Number(c.beneficiarios_directos) || 0) + (Number(c.beneficiarios_indirectos) || 0), 0
    ) + projects.reduce(
      (s, p) => s + (Number(p.beneficiarios_directos) || 0) + (Number(p.beneficiarios_indirectos) || 0), 0
    );
    const presupuestoProj = projects.reduce((s, p) => s + (Number(p.presupuesto) || 0), 0);
    const ejecutadoProj = projects.reduce((s, p) => s + (Number(p.inversion_ejecutada) || 0), 0);
    const ejecRate = presupuestoProj ? Math.round((ejecutadoProj / presupuestoProj) * 100) : 0;
    return {
      overdue, due30, completed, completionRate, avgAvance, avgProjAvance,
      totalBeneficiarios, presupuestoProj, ejecutadoProj, ejecRate,
    };
  }, [commitments, projects]);

  // distributions
  const byEstado = useMemo(() => {
    const map = new Map();
    commitments.forEach((c) => map.set(c.estado, (map.get(c.estado) || 0) + 1));
    const colors = {
      cumplido: "bg-brand-green-500",
      en_proceso: "bg-sky-500",
      pendiente: "bg-amber-500",
      atrasado: "bg-red-500",
      cancelado: "bg-slate-400",
      incumplido: "bg-red-700",
    };
    return Array.from(map.entries()).map(([k, v]) => ({
      label: formatLabel(k), value: v, color: colors[k] || "bg-slate-500",
    }));
  }, [commitments]);

  const byTipo = useMemo(() => {
    const map = new Map();
    commitments.forEach((c) => map.set(c.tipo, (map.get(c.tipo) || 0) + 1));
    const palette = ["bg-indigo-500","bg-fuchsia-500","bg-teal-500","bg-amber-500","bg-rose-500","bg-cyan-600"];
    return Array.from(map.entries()).map(([k, v], i) => ({
      label: formatLabel(k), value: v, color: palette[i % palette.length],
    }));
  }, [commitments]);

  const bySemaforo = useMemo(() => {
    const map = { verde: 0, amarillo: 0, rojo: 0 };
    commitments.forEach((c) => { if (map[c.semaforo] !== undefined) map[c.semaforo]++; });
    return [
      { label: "Vigente", value: map.verde, color: "bg-brand-green-500" },
      { label: "Próximo", value: map.amarillo, color: "bg-amber-500" },
      { label: "Crítico", value: map.rojo, color: "bg-red-500" },
    ];
  }, [commitments]);

  const investmentByEje = useMemo(() => {
    const map = new Map();
    projects.forEach((p) => {
      const eje = p.eje_estrategico || "Sin eje";
      map.set(eje, (map.get(eje) || 0) + (Number(p.presupuesto) || 0));
    });
    const palette = ["bg-brand-green-600","bg-sky-600","bg-violet-600","bg-amber-600","bg-rose-600"];
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([k, v], i) => ({ label: formatLabel(k), value: v, color: palette[i % palette.length] }));
  }, [projects]);

  const upcomingCommitments = commitments
    .filter((c) => c.fecha_compromiso && c.estado !== "cumplido")
    .map((c) => ({ ...c, _dl: daysLeft(c.fecha_compromiso) }))
    .filter((c) => c._dl !== null && c._dl >= -30)
    .sort((a, b) => a._dl - b._dl)
    .slice(0, 6);

  const recentMeetings = [...meetings].slice(0, 4);

  // Timeline: commitments due per month (next 12 months) + completed per month (last 12)
  const monthly = useMemo(() => {
    const map = new Map();
    const monthKey = (d) => {
      const dt = new Date(d);
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
    };
    const monthLabel = (key) => {
      const [y, m] = key.split("-");
      return new Date(+y, +m - 1, 1).toLocaleDateString("es-CL", { month: "short", year: "2-digit" });
    };
    commitments.forEach((c) => {
      if (c.fecha_compromiso) {
        const k = monthKey(c.fecha_compromiso);
        if (!map.has(k)) map.set(k, { mes: monthLabel(k), key: k, vencen: 0, cumplidos: 0 });
        map.get(k).vencen += 1;
      }
      if (c.estado === "cumplido" && c.fecha_cumplimiento) {
        const k = monthKey(c.fecha_cumplimiento);
        if (!map.has(k)) map.set(k, { mes: monthLabel(k), key: k, vencen: 0, cumplidos: 0 });
        map.get(k).cumplidos += 1;
      }
    });
    return Array.from(map.values()).sort((a, b) => a.key.localeCompare(b.key));
  }, [commitments]);

  // Project progress chart data
  const projectProgress = useMemo(
    () => projects.map((p) => ({
      name: p.name.length > 22 ? p.name.slice(0, 22) + "…" : p.name,
      operativo: Number(p.percent_avance) || 0,
      financiero: p.presupuesto ? Math.round(((Number(p.inversion_ejecutada) || 0) / Number(p.presupuesto)) * 100) : 0,
    })),
    [projects]
  );

  const tooltipStyle = {
    contentStyle: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 },
    labelStyle: { color: "#0f172a", fontWeight: 600 },
  };

  return (
    <div className="space-y-6 mt-4">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniStat icon={CheckCircle} label="Cumplimiento" value={`${k.completionRate}%`}
          hint={`${k.completed.length} de ${commitments.length} compromisos cumplidos`} tone="emerald" />
        <MiniStat icon={TrendUp} label="Avance promedio" value={`${k.avgAvance}%`}
          hint={`Proyectos: ${k.avgProjAvance}%`} tone="sky" />
        <MiniStat icon={Warning} label="Atrasados" value={k.overdue.length}
          hint={k.due30.length ? `${k.due30.length} vencen en ≤ 30 días` : "Sin compromisos próximos a vencer"}
          tone={k.overdue.length ? "red" : "slate"} />
        <MiniStat icon={Users} label="Beneficiarios" value={k.totalBeneficiarios.toLocaleString("es-CL")}
          hint="Directos + indirectos (compromisos y proyectos)" />
      </div>

      {/* Inversión summary */}
      <Card className="rounded-md">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><CurrencyCircleDollar size={18} weight="duotone" className="text-brand-orange-500" /> Inversión</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Total comprometido</div>
              <div className="text-xl font-bold font-display mt-1">{formatCLP(community.inversion_total || 0)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Presupuesto proyectos</div>
              <div className="text-xl font-bold font-display mt-1">{formatCLP(k.presupuestoProj)}</div>
              <div className="text-xs text-slate-500">Ejecutado: {formatCLP(k.ejecutadoProj)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">% Ejecución financiera</div>
              <div className="text-xl font-bold font-display mt-1">{k.ejecRate}%</div>
              <div className="mt-2"><Progress value={k.ejecRate} /></div>
            </div>
          </div>
          {investmentByEje.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-slate-600 mb-2">Inversión por eje estratégico (CLP)</div>
              <div style={{ width: "100%", height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={investmentByEje.map((r) => ({ eje: r.label, monto: r.value, fill: hexOf(r.color) }))}
                    margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="eje" tick={{ fontSize: 11, fill: "#475569" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#475569" }}
                      tickFormatter={(v) => v >= 1e6 ? `$${(v / 1e6).toFixed(0)}M` : `$${v}`} />
                    <Tooltip {...tooltipStyle} formatter={(v) => formatCLP(v)} />
                    <Bar dataKey="monto" radius={[4, 4, 0, 0]}>
                      {investmentByEje.map((r, i) => <Cell key={i} fill={hexOf(r.color)} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timeline line chart */}
      {monthly.length > 1 && (
        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendUp size={18} weight="duotone" className="text-brand-green-600" /> Evolución temporal de compromisos</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <LineChart data={monthly} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="mes" tick={{ fontSize: 11, fill: "#475569" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#475569" }} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="vencen" name="Vencen" stroke="#0ea5e9" strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="cumplidos" name="Cumplidos" stroke="#85c536" strokeWidth={2.5} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Distribution charts: bar + bar + pie */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><ListChecks size={18} weight="duotone" className="text-brand-orange-500" /> Por estado</CardTitle></CardHeader>
          <CardContent>
            {byEstado.length === 0 ? <div className="text-sm text-slate-400">Sin datos.</div> : (
              <div style={{ width: "100%", height: 200 }}>
                <ResponsiveContainer>
                  <BarChart data={byEstado} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: "#475569" }} allowDecimals={false} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 11, fill: "#475569" }} width={90} />
                    <Tooltip {...tooltipStyle} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {byEstado.map((r, i) => <Cell key={i} fill={hexOf(r.color)} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-base">Por tipo</CardTitle></CardHeader>
          <CardContent>
            {byTipo.length === 0 ? <div className="text-sm text-slate-400">Sin datos.</div> : (
              <div style={{ width: "100%", height: 200 }}>
                <ResponsiveContainer>
                  <BarChart data={byTipo} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#475569" }} interval={0} angle={-15} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 10, fill: "#475569" }} allowDecimals={false} />
                    <Tooltip {...tooltipStyle} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {byTipo.map((r, i) => <Cell key={i} fill={hexOf(r.color)} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-base">Semáforo</CardTitle></CardHeader>
          <CardContent>
            {commitments.length === 0 ? <div className="text-sm text-slate-400">Sin datos.</div> : (
              <div style={{ width: "100%", height: 200 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={bySemaforo.filter((r) => r.value > 0)} dataKey="value" nameKey="label" cx="50%" cy="50%" innerRadius={40} outerRadius={75} paddingAngle={3}>
                      {bySemaforo.map((r, i) => <Cell key={i} fill={hexOf(r.color)} />)}
                    </Pie>
                    <Tooltip {...tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming & recent meetings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Clock size={18} weight="duotone" className="text-amber-500" /> Próximos vencimientos</CardTitle></CardHeader>
          <CardContent>
            {upcomingCommitments.length === 0 && <div className="text-sm text-slate-400">No hay compromisos próximos a vencer.</div>}
            <ul className="divide-y divide-slate-100">
              {upcomingCommitments.map((c) => {
                const tone = c._dl < 0 ? "text-red-600 font-semibold" : c._dl <= 3 ? "text-red-600" : c._dl <= 7 ? "text-amber-600" : "text-slate-700";
                const label = c._dl < 0 ? `Atrasado ${Math.abs(c._dl)}d` : c._dl === 0 ? "Hoy" : `${c._dl}d`;
                return (
                  <li key={c.id} className="py-2 flex items-center justify-between gap-3">
                    <Link to={`/compromisos/${c.id}`} className="text-sm hover:underline truncate" title={c.name}>{c.name}</Link>
                    <div className="flex items-center gap-2 text-xs whitespace-nowrap">
                      <span className="text-slate-500">{formatDate(c.fecha_compromiso)}</span>
                      <span className={tone}>{label}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>

        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar size={18} weight="duotone" className="text-sky-500" /> Reuniones recientes</CardTitle></CardHeader>
          <CardContent>
            {recentMeetings.length === 0 && <div className="text-sm text-slate-400">Sin reuniones registradas.</div>}
            <ul className="divide-y divide-slate-100">
              {recentMeetings.map((m) => (
                <li key={m.id} className="py-2 flex items-center justify-between gap-3">
                  <Link to={`/reuniones/${m.id}`} className="text-sm hover:underline truncate" title={m.tema || "Reunión"}>{m.tema || "Reunión"}</Link>
                  <div className="flex items-center gap-2 text-xs">
                    {m.tipo && <StatusBadge tone="info">{m.tipo}</StatusBadge>}
                    <span className="text-slate-500 whitespace-nowrap">{formatDate(m.fecha)}</span>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Projects progress chart */}
      {projects.length > 0 && (
        <Card className="rounded-md">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Briefcase size={18} weight="duotone" className="text-brand-green-600" /> Avance de proyectos (operativo vs financiero)</CardTitle></CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: Math.max(220, projects.length * 60) }}>
              <ResponsiveContainer>
                <BarChart data={projectProgress} layout="vertical" margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "#475569" }} tickFormatter={(v) => `${v}%`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} width={180} />
                  <Tooltip {...tooltipStyle} formatter={(v) => `${v}%`} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="operativo" name="Avance operativo" fill="#85c536" radius={[0, 3, 3, 0]} />
                  <Bar dataKey="financiero" name="Avance financiero" fill="#ff751f" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <ul className="mt-4 space-y-2">
              {projects.map((p) => (
                <li key={p.id} className="flex items-center justify-between text-sm border-t border-slate-100 pt-2">
                  <Link to={`/proyectos/${p.id}`} className="hover:underline font-medium truncate">{p.name}</Link>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500 font-mono">{formatCLP(p.presupuesto || 0)}</span>
                    <Semaforo value={p.semaforo} />
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
